import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  ArrowUpDown,
  Edit,
} from "lucide-react";
import { useLazyGetCartsForRestaurantByRestaurantIdQuery } from "../../../redux/services/restaurantServices";
import { CartConfiguration } from "../../../redux/cartConfigurationSlice";
import {
  calculateDefaultPriority,
  getPrioritySortValue,
} from "../../../dummyData/distributionPlan";
import BulkEditPanel from "./BulkEditPanel";
import CartTable from "./CartTable";
import CartEditModal from "./CartEditModal";
import "./index.css";
import TableLoadingComponent from "../../../components/TableLoadingComponent";
import { useUpdateBulkCartLoadingConfigurationMutation } from "../../../redux/services/DistributionService";
import { toast } from "react-toastify";
import BreadCrumbs from "../../../components/BreadCrumbs";

const CartConfigurations: React.FC = () => {
  const [carts, setCarts] = useState<CartConfiguration[]>([]);
  const [availableGates] = useState<string[]>(["A", "B", "C", "D", "E"]);

  const [editingCart, setEditingCart] = useState<CartConfiguration | null>(
    null
  );
  const [bulkEditMode, setBulkEditMode] = useState<boolean>(false);
  const [selectedCarts, setSelectedCarts] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<string>("priority");

    const [triggerGetCarts, { data: cartsData, isLoading, error }] =
    useLazyGetCartsForRestaurantByRestaurantIdQuery();
  
  const [updateBulkCartLoadingConfiguration, { isLoading: isUpdating }] = 
    useUpdateBulkCartLoadingConfigurationMutation();

  // Fetch carts when component mounts or restaurantId changes

    const RestID = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );

  useEffect(() => {
    if (RestID) {
      triggerGetCarts(RestID);
    }
  }, [RestID, triggerGetCarts]);

  // Transform API data to CartConfiguration format
  // In your CartConfigurations component
  useEffect(() => {
    if (cartsData) {
      const transformedCarts: CartConfiguration[] = cartsData.map(
        (cart: any) => {
          const priority = cart.loadingConfigurations?.priority || "";

          // Handle Firestore timestamp for leaveTime
          let leaveTime = null;
          if (cart.loadingConfigurations?.leaveTime) {
            if (cart.loadingConfigurations.leaveTime._seconds) {
              // Convert Firestore timestamp to ISO string
              leaveTime = new Date(
                cart.loadingConfigurations.leaveTime._seconds * 1000 +
                  (cart.loadingConfigurations.leaveTime._nanoseconds || 0) /
                    1000000
              ).toISOString();
            } else if (
              typeof cart.loadingConfigurations.leaveTime === "string"
            ) {
              leaveTime = cart.loadingConfigurations.leaveTime;
            }
          }

          return {
            id: cart.id,
            rider: cart.owner?.email?.split("@")[0] || "Unknown Rider",
            route: cart.displayName || "Unknown Route",
            distance:
              cart.loadingConfigurations?.distance || cart.distance || 0,
            status: cart.cartState === "Online" ? "available" : "offline",
            priority: priority,
            leaveTime: leaveTime,
            assignedGate: cart.loadingConfigurations?.assignedGate || "",
            cartType: cart.loadingConfigurations?.cartType || "",
            notes: cart.reasonForBeingOffline || "",
            displayName: cart.displayName,
            owner: cart.owner,
          };
        }
      );
      setCarts(transformedCarts);
    }
  }, [cartsData]);

  // Reset cart priorities to distance-based defaults
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const resetToDistancePriorities = () => {
    setCarts((prev) =>
      prev.map((cart) => ({
        ...cart,
        priority: calculateDefaultPriority(
          cart.distance || 0,
          prev.map((c) => c.distance || 0)
        ),
      }))
    );
  };

  // Auto-assign gates based on priority
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const autoAssignGates = () => {
    const sortedCarts = [...carts].sort(
      (a, b) =>
        getPrioritySortValue(a.priority) - getPrioritySortValue(b.priority)
    );
    const updatedCarts = carts.map((cart) => {
      const cartIndex = sortedCarts.findIndex((c) => c.id === cart.id);
      const gateIndex = cartIndex % availableGates.length;
      return {
        ...cart,
        assignedGate: availableGates[gateIndex],
      };
    });
    setCarts(updatedCarts);
  };

  // Update cart configuration
  const updateCart = (cartId: string, updates: Partial<CartConfiguration>) => {
    setCarts((prev) =>
      prev.map((cart) => (cart.id === cartId ? { ...cart, ...updates } : cart))
    );
  };

  // Bulk update selected carts
 // In CartConfigurations component, update the bulkUpdateCarts function:

const bulkUpdateCarts = async (updates: Partial<CartConfiguration>) => {
  try {
    // Get the current cart data for selected carts
    const selectedCartData = carts.filter(cart => selectedCarts.has(cart.id));
    
    // Prepare the updates for the API - preserve existing values for non-updated fields
    const bulkUpdates = selectedCartData.map(cart => ({
      cartId: cart.id,
      priority: updates.priority !== undefined && updates.priority !== '' 
        ? (updates.priority as "High" | "Medium" | "Low") 
        : (cart.priority as "High" | "Medium" | "Low" || ""),
      assignedGate: updates.assignedGate !== undefined && updates.assignedGate !== ''
        ? updates.assignedGate
        : cart.assignedGate || "Unassigned",
      cartType: updates.cartType !== undefined && updates.cartType !== ''
        ? updates.cartType
        : cart.cartType || ""
    }));

    // Filter out updates where no changes are actually being made
    const meaningfulUpdates = bulkUpdates.filter(update => {
      const originalCart = selectedCartData.find(cart => cart.id === update.cartId);
      return (
        (updates.priority !== undefined && updates.priority !== '' && update.priority !== originalCart?.priority) ||
        (updates.assignedGate !== undefined && updates.assignedGate !== '' && update.assignedGate !== originalCart?.assignedGate) ||
        (updates.cartType !== undefined && updates.cartType !== '' && update.cartType !== originalCart?.cartType)
      );
    });

    if (meaningfulUpdates.length === 0) {
      toast.error("No changes to apply. Please select at least one field to update.");
      return;
    }

    // Call the API
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const response = await updateBulkCartLoadingConfiguration({
      updates: meaningfulUpdates
    }).unwrap();
    
    // Update local state only if API call is successful
    setCarts((prev) =>
      prev.map((cart) =>
        selectedCarts.has(cart.id) 
          ? { 
              ...cart, 
              priority: updates.priority !== undefined && updates.priority !== '' ? updates.priority : cart.priority,
              assignedGate: updates.assignedGate !== undefined && updates.assignedGate !== '' ? updates.assignedGate : cart.assignedGate,
              cartType: updates.cartType !== undefined && updates.cartType !== '' ? updates.cartType : cart.cartType
            } 
          : cart
      )
    );
    
    // Show success message
    toast.success(`Bulk update applied successfully to ${meaningfulUpdates.length} carts!`);
    
    // Close bulk edit mode
    setBulkEditMode(false);
    
  } catch (error) {
    console.error("Failed to bulk update carts:", error);
    toast.error("Failed to apply bulk updates. Please try again.");
  }
};

  if (error) {
    return (
      <div className="cart-configuration-error">
        Error loading cart configurations: {error.toString()}
      </div>
    );
  }

  return (
    <>
    <BreadCrumbs hasSubMenus={true}/>
    <div className="cart-configuration-management-container">
      <div className="cart-configuration-management-wrapper">
       
          <header className="cart-configuration-management-header">
            <div className="cart-configuration-management-header-title">
             
              <h1>Cart Configuration Management</h1>
            </div>
            <p className="cart-configuration-management-header-description">
              Configure cart priorities, schedules, and gate assignments before
              loading begins
            </p>
          </header>
          {/* Control Panel */}
          <div className="cart-configuration-control-panel">
            <div className="cart-configuration-control-left">
              <div className="cart-configuration-sort-control">
                <ArrowUpDown className="cart-configuration-control-icon" />
                <label>Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="priority">Priority</option>
                  <option value="distance">Distance</option>
                  <option value="leaveTime">Leave Time</option>
                  <option value="cartId">Cart ID</option>
                </select>
              </div>

                <button
                onClick={() => setBulkEditMode(!bulkEditMode)}
                className={`cart-configuration-bulk-edit-btn ${
                  bulkEditMode ? "active" : ""
                }`}
                disabled={isUpdating}
              >
                <Edit className="cart-configuration-bulk-edit-icon" />
                {isUpdating ? "Updating..." : "Bulk Edit"}
              </button>
            </div>
          </div>

          {/* Bulk Edit Panel */}
          {bulkEditMode && (
            <BulkEditPanel
              selectedCarts={selectedCarts}
              setBulkEditMode={setBulkEditMode}
              bulkUpdateCarts={bulkUpdateCarts}
              availableGates={availableGates}
              isUpdating={isUpdating}
            />
          )}

          {/* Priority Legend
          <div className="cart-configuration-priority-legend">
            <span className="cart-configuration-legend-title">Priority Levels:</span>
            {[1, 2, 3, 4].map(priority => (
              <div key={priority} className="cart-configuration-legend-item">
                <div className={`cart-configuration-priority-dot ${priority}`}></div>
                <span className="cart-configuration-legend-text">{priority} - {priority}</span>
              </div>
            ))}
          </div> */}

          {/* Cart Table */}
          {isLoading ? (
            <div className="cart-configuration-table-loading">
              <TableLoadingComponent numColumns={8} />
            </div>
          ) : (
            <CartTable
              carts={carts}
              bulkEditMode={bulkEditMode}
              selectedCarts={selectedCarts}
              setSelectedCarts={setSelectedCarts}
              setEditingCart={setEditingCart}
              sortBy={sortBy}
              availableGates={availableGates}
            />
          )}

        {/* Edit Modal */}
        {editingCart && (
          <CartEditModal
            cart={editingCart}
            onClose={() => setEditingCart(null)}
            onSave={updateCart}
            availableGates={availableGates}
          />
        )}
      </div>
    </div>
    </>
  );
};

export default CartConfigurations;
